import type { MediaDetails, MediaEvent, MediaRequest, Trip, Vehicle } from '@cobliteam/common-types';
import { endOfDay, startOfDay } from '@cobliteam/utils/date';

// Types
interface TimeMarker {
  time: number;
  percent: number;
  isMajor: boolean;
}

interface ViewWindow {
  start: number;
  end: number;
}

interface PeriodOption {
  id: string;
  label: string;
  value: string;
  isDisabled: boolean;
  trip: Trip;
  overnightTripStartTimestamp?: number;
}

interface VehicleGroupOption {
  value: string;
  label: string;
  vehicles: Vehicle[];
}

// Constants
const LEGEND_ITEMS = [
  { id: 'no-activity', icon: 'square', color: 'gray-4', label: 'Sem atividade' },
  { id: 'recent', icon: 'circle', color: 'primary-6', label: 'Recente' },
  { id: 'next-to-expire', icon: 'circle', color: 'accessory-3', label: 'Próximo a vencer' },
  { id: 'expired', icon: 'circle', color: 'gray', label: 'Gravação Expirada' },
];

const STEPS = [
  {
    id: 'vehicle-and-date',
    title: 'Veículo e data',
  },
  {
    id: 'media-type',
    title: 'Categoria do vídeo',
  },
  {
    id: 'video-selection',
    title: 'Seleção do vídeo',
  },
  {
    id: 'request-confirmation',
    title: 'Confirmação de solicitação',
  },
];

// Helper functions
const getTripsKey = (trips: Trip[] | undefined): string => {
  if (!trips?.length) return '';

  return trips.map((trip) => `${trip.startTimestamp}-${trip.endTimestamp}-${trip.isFootageAvailable}`).join('|');
};

const normalizeTripsData = (trips: Trip[]): Trip[] => {
  return trips.flatMap((trip) => {
    const tripStart = startOfDay(new Date(trip.startTimestamp));
    const tripEnd = startOfDay(new Date(trip.endTimestamp));

    if (tripEnd > tripStart) {
      return [
        {
          ...trip,
          endTimestamp: endOfDay(tripStart).getTime(),
        },
        {
          ...trip,
          startTimestamp: tripEnd.getTime(),
        },
      ];
    }
    return [trip];
  });
};

// Type guards to verify media details typings
const isMediaEvent = (details: MediaDetails): details is MediaEvent => 'eventTrigger' in details;
const isMediaFootage = (details: MediaDetails): details is MediaRequest => 'duration' in details;

export { LEGEND_ITEMS, STEPS };
export { getTripsKey, normalizeTripsData, isMediaEvent, isMediaFootage };
export type { PeriodOption, VehicleGroupOption };
